import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, List, ListItem, Typography } from '@mui/material';
import { BUSINESS_PLAN_TYPES, useBusinessPlanOptions } from 'constants/businessPlan';
import { BUSINESS_TOOL_MODE } from 'constants/businessTool';
import * as PATH from 'constants/paths';
import { useCreateBusinessPlan } from 'services/businessPlan/hooks';
import { useBusinessToolSessionStore, useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan, getSetBusinessToolMode } from 'store/useBusinessToolStore/selectors';
import { useUserLimits } from 'store/useUserLimits';
import { getUserLimits } from 'store/useUserLimits/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { IBusinessPlanType, ILimitEntity } from 'interfaces/businessPlan';

import { BottomSliderControlled } from 'components/_basic/BottomSlider';
import { Button } from 'components/_basic/Button/Button';

import { getDocumentType } from 'helpers/getDocumentType';
import { getValidatedMetadata } from 'helpers/metadataParser';
import { insertId } from 'helpers/path';
import ToastService from 'helpers/ToastService';

import { CreateProjectButtonEmptyWS, CreateProjectButtonFixed, SelectTemplateMobileButton } from './components';

import styles from './CreateProjectMobile.module.scss';

interface IProps {
  isButtonFixed?: boolean;
}

export const CreateProjectMobile: FC<IProps> = ({ isButtonFixed = false }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<IBusinessPlanType>(BUSINESS_PLAN_TYPES.FOR_BANK);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, isAuthConfiguring } = useAuthenticated();
  const createBusinessPlan = useCreateBusinessPlan();
  const setBusinessToolMode = useBusinessToolSessionStore(getSetBusinessToolMode);
  const metadata = useBusinessToolStore(getBusinessPlan).metadata;

  const limits = useUserLimits(getUserLimits);
  const businessPlanOptions = useBusinessPlanOptions();

  const isAnyDocAvailable = (Object.values(limits?.availableDocumentCount) as number[]).some((item) => item > 0);

  const getIsAvailableBusinessPlan = () =>
    limits.availableDocumentCount[getDocumentType(selectedTemplate) as keyof ILimitEntity] > 0;

  const updatePlanHandler = () => navigate(PATH.SUBSCRIPTION_PLAN);

  const applyTemplateHandler = async () => {
    setBusinessToolMode(BUSINESS_TOOL_MODE.EDIT);

    if (!(isAuthenticated || isAuthConfiguring)) return navigate(PATH.HOME);

    if (!getIsAvailableBusinessPlan() || createBusinessPlan.isPending) return;

    try {
      const newBusinessPlan = await createBusinessPlan.mutateAsync({
        title: 'Document',
        type: selectedTemplate,
        sections: [],
        metadata: getValidatedMetadata(metadata),
      });

      navigate(insertId(PATH.BUSINESS_TOOL, newBusinessPlan.uuid));
    } catch (error) {
      error instanceof Error && ToastService.showError(error.message);
    }
  };

  return (
    <BottomSliderControlled
      renderController={(onClick) =>
        isButtonFixed ? (
          <CreateProjectButtonFixed onClick={onClick} disabled={!isAnyDocAvailable} />
        ) : (
          <CreateProjectButtonEmptyWS onClick={onClick} />
        )
      }
    >
      {({ onClose }) => (
        <>
          <Typography variant="h5">{t('selectTemplate')}</Typography>
          <Box className={styles.contentWrapper}>
            {!getIsAvailableBusinessPlan() && (
              <Typography variant="body2">{t('maxNUmberOfProjectsIsCreated')}</Typography>
            )}
            <List className={styles.listWrapper}>
              {businessPlanOptions.map((item) => {
                return (
                  <ListItem key={item.template} className={styles.listItem}>
                    <SelectTemplateMobileButton
                      {...item}
                      isSelected={item.template === selectedTemplate}
                      setSelectedTemplate={setSelectedTemplate}
                    />
                  </ListItem>
                );
              })}
            </List>
            {getIsAvailableBusinessPlan() ? (
              <Button
                onClick={applyTemplateHandler}
                disabled={createBusinessPlan.isPending}
                isLoading={createBusinessPlan.isPending}
                variant="contained"
                fullWidth
              >
                {t('apply')}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  updatePlanHandler();
                  onClose();
                }}
                variant="outlined"
                color="secondary"
                fullWidth
              >
                {t('upgradePlan')}
              </Button>
            )}
          </Box>
        </>
      )}
    </BottomSliderControlled>
  );
};
